import * as React from 'react';
import AppBar from '@material-ui/core/AppBar';
import Grid from '@material-ui/core/Grid';
import Tab from '@material-ui/core/Tab';
import Tabs from '@material-ui/core/Tabs';
import Toolbar from '@material-ui/core/Toolbar';
import Typography from '@material-ui/core/Typography';
import {
  createStyles,
  Theme,
  withStyles,
  WithStyles,
} from '@material-ui/core/styles';
import { PageType } from './Paperbase';
import { useRouteMatch, Link as RouterLink, useLocation } from 'react-router-dom';
import { NavigatorData, Operations } from './NavigatorData';
import { Button, ButtonGroup, FormControlLabel, Radio, RadioGroup } from '@material-ui/core';
import { AuthContext } from './AuthContext';
import LockOpenIcon from '@material-ui/icons/LockOpen';
import { OperationContext } from './OperationContext';
import { Themes } from '../styling/Themes';
import { DeltaXMLColors, DeltaJSONColors } from '../styling/DeltaColors';
import Profile, { ProfileDetails } from './Profile';
import { CustomerSubscription, CustomerSubscriptionStatus } from './SubscriptionData';

const styles = (theme: Theme) =>
  createStyles({
    secondaryBar: {
      zIndex: 0,
      backgroundColor: DeltaXMLColors.DARK_BACKGROUND
    },
    toolBar: {
      borderLeftStyle: 'solid',
      borderWidth: '0.5px',
      borderColor: DeltaXMLColors.OXFORD_BLUE,
      color: DeltaXMLColors.CELERY
    }
  });

interface HeaderProps extends WithStyles<typeof styles> {
  profileDetails: ProfileDetails;
  type: PageType;
  themeChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  themeType: string;
  customerSubscriptionData: CustomerSubscription
}

function Header(props: HeaderProps) {
  const { classes, type, themeChange, themeType, profileDetails, customerSubscriptionData } = props;
  const hanldeTheme = themeChange;
  const [currentTabname, setCurrentTabname] = React.useState(Operations.compare);
  const location = useLocation();
  const locationParts = location.pathname.split('/');
  const lastPart = '/' + locationParts[locationParts.length - 1];
  const opContext = React.useContext(OperationContext);
  const auth = React.useContext(AuthContext);
  const [authButton, setAuthButton] = React.useState(<></>);
  const starterSubscription: boolean = customerSubscriptionData.subscriptionStatus === CustomerSubscriptionStatus.FREE;
  const professionalSubscription: boolean = customerSubscriptionData.subscriptionStatus === CustomerSubscriptionStatus.PAID;
  const compareEnabled = starterSubscription || professionalSubscription;
  const otherOperationsEnabled = professionalSubscription;
  let timer = 50;

  const authenticationTimer = (count: number) => {
    setTimeout(() => {
      if (auth.isAuthenticated()) {
        setAuthButton(<Profile profileDetails={profileDetails} customerSubscriptionData={customerSubscriptionData}/>)  
      } else if (count > 0) {
        timer = timer * 2;
        authenticationTimer(count -1);
      } else {
        auth.login(); 
      }
    }, timer);
  }

  React.useEffect(() => {
    authenticationTimer(4);
    timer = 50;
  },[profileDetails, customerSubscriptionData]);

  function login() { auth.login(); }

  React.useEffect(() => {
    const tabIndex = NavigatorData.getOperationForPath(lastPart);
    setCurrentTabname(tabIndex);
  }, [lastPart]);

  let pageTitle;
  switch (type) {
    case PageType.Welcome:
      pageTitle = 'Welcome';
      break;
    case PageType.Configuration:
      pageTitle = 'Configuration'
      break;
    case PageType.Files:
      pageTitle = 'Files'
      break;
    case PageType.Logs:
      pageTitle = 'Logs'
      break;
    case PageType.SessionJobs:
      pageTitle = 'Session Jobs'
      break;
    case PageType.JobsHistory:
      pageTitle = "Jobs History"
      break;
  }
  let { url } = useRouteMatch();

  let tabToolTipMessage = <Typography color="inherit" variant="body2">Your current plan does not permit you to perform this operation.</Typography>;

  return (
    <React.Fragment>
      <AppBar
        className={classes.secondaryBar}
        position="static"
        elevation={0}
      >
        {type === PageType.Files &&
          <Toolbar className={classes.toolBar}>
            <Grid container alignItems="center" spacing={1}>
              <Grid item xs>
                <Grid container spacing={1} alignItems="center">
                  <Grid item>
                    <Typography color="inherit" variant="h4">
                      {pageTitle}
                    </Typography>
                  </Grid>
                  <Grid item xs >
                    <Typography color="inherit" variant="body1" style={{ marginTop: '4px', marginLeft: '20px' }}>
                      [ {opContext.operationType} ]
                    </Typography>
                  </Grid>
                  <RadioGroup row aria-label="theme-settings" name="themes" value={themeType} onChange={hanldeTheme}>
                    <FormControlLabel value="light" control={<Radio color="primary" />} label="Light" style={{ color: DeltaJSONColors.WHITE }} />
                    <FormControlLabel value="dark" control={<Radio color="primary" />} label="Dark" style={{ color: DeltaJSONColors.WHITE }} />
                  </RadioGroup>
                  {/* <Grid item>
                    <ButtonGroup variant="contained" >
                    <Tooltip title="Command Palette (F1)">
                      <IconButton color="inherit" >
                        <PlaylistPlayIcon />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Format JSON (⇧⌥F)">
                      <IconButton color="inherit" >
                        <FormatAlignRightIcon />
                      </IconButton>
                    </Tooltip>
                    </ButtonGroup>
                    </Grid> */}
                  <Grid item>
                    { authButton }
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
          </Toolbar>
        }
        {type !== PageType.Files &&
          <Toolbar className={classes.toolBar}>
            <Grid container alignItems="center" spacing={1}>
              <Grid item xs>
                <Typography color="inherit" variant="h4">
                  {pageTitle}
                </Typography>
              </Grid>
              <Grid item>
                { authButton }
              </Grid>
            </Grid>
          </Toolbar>
        }
      </AppBar>
      {(type === PageType.Configuration || type === PageType.JobsHistory || type === PageType.SessionJobs) &&
        <AppBar
          className={classes.secondaryBar}
          position="static"
          elevation={0}
        >
          <Tabs value={currentTabname} selectionFollowsFocus>
            {
              (compareEnabled) ?
                <Tab value={Operations.compare} label="Compare" component={RouterLink} to={`${url}${NavigatorData.getPathForOperation(Operations.compare)}`} /> :
                <Themes.HtmlTooltip title={tabToolTipMessage} arrow>
                  <span>
                    <Tab value={Operations.compare} label="Compare" component={RouterLink} to={`${url}${NavigatorData.getPathForOperation(Operations.compare)}`} disabled={!compareEnabled} />
                  </span>
                </Themes.HtmlTooltip>
            }
            {
              (otherOperationsEnabled) ?
                <Tab value={Operations.twoWayMerge} label="Two Way Merge" component={RouterLink} to={`${url}${NavigatorData.getPathForOperation(Operations.twoWayMerge)}`} /> :
                <Themes.HtmlTooltip title={tabToolTipMessage} arrow>
                  <span>
                    <Tab value={Operations.twoWayMerge} label="Two Way Merge" component={RouterLink} to={`${url}${NavigatorData.getPathForOperation(Operations.twoWayMerge)}`} disabled={!otherOperationsEnabled} />
                  </span>
                </Themes.HtmlTooltip>
            }
            {
              (otherOperationsEnabled) ?
                <Tab value={Operations.threeWayMerge} label="Three Way Merge" component={RouterLink} to={`${url}${NavigatorData.getPathForOperation(Operations.threeWayMerge)}`} disabled={!otherOperationsEnabled} /> :
                <Themes.HtmlTooltip title={tabToolTipMessage} arrow>
                  <span>
                    <Tab value={Operations.threeWayMerge} label="Three Way Merge" component={RouterLink} to={`${url}${NavigatorData.getPathForOperation(Operations.threeWayMerge)}`} disabled={!otherOperationsEnabled} />
                  </span>
                </Themes.HtmlTooltip>
            }
            {
              (otherOperationsEnabled) ?
                <Tab value={Operations.graft} label="Graft" component={RouterLink} to={`${url}${NavigatorData.getPathForOperation(Operations.graft)}`} disabled={!otherOperationsEnabled} /> :
                <Themes.HtmlTooltip title={tabToolTipMessage} arrow>
                  <span>
                    <Tab value={Operations.graft} label="Graft" component={RouterLink} to={`${url}${NavigatorData.getPathForOperation(Operations.graft)}`} disabled={!otherOperationsEnabled} />
                  </span>
                </Themes.HtmlTooltip>
            }
            {
              (otherOperationsEnabled) ?
                <Tab value={Operations.patch} label="Create Patch" component={RouterLink} to={`${url}${NavigatorData.getPathForOperation(Operations.patch)}`} disabled={!otherOperationsEnabled} /> :
                <Themes.HtmlTooltip title={tabToolTipMessage} arrow>
                  <span>
                    <Tab value={Operations.patch} label="Create Patch" component={RouterLink} to={`${url}${NavigatorData.getPathForOperation(Operations.patch)}`} disabled={!otherOperationsEnabled} />
                  </span>
                </Themes.HtmlTooltip>
            }
          </Tabs>
        </AppBar>

      }
    </React.Fragment>
  );
}

export default withStyles(styles)(Header);

